<script>
    import { createEventDispatcher } from 'svelte';
    import { fade } from 'svelte/transition';
    import { boilerplatesStore } from '../../stores/boilerplatesStore';
    import AccessibleModal from '../shared/AccessibleModal.svelte';

    const dispatch = createEventDispatcher();

    export let show = false;

    let newBoilerplateName = '';
    let newBoilerplateContent = '';
    let showAddForm = false;
    let editingBoilerplateId = null;
    let editingBoilerplateName = '';
    let editingBoilerplateContent = '';

    function closeModal() {
        show = false;
        dispatch('close');
    }

    function toggleAddForm() {
        showAddForm = !showAddForm;
        if (!showAddForm) {
            newBoilerplateName = '';
            newBoilerplateContent = '';
        }
        // Cancel any ongoing edit
        editingBoilerplateId = null;
    }

    async function handleAddBoilerplate() {
        if (!newBoilerplateName.trim() || !newBoilerplateContent.trim()) return;

        await boilerplatesStore.addBoilerplate({
            name: newBoilerplateName,
            content: newBoilerplateContent,
            createdAt: new Date().toISOString()
        });

        newBoilerplateName = '';
        newBoilerplateContent = '';
        showAddForm = false;
    }

    async function handleDeleteBoilerplate(id) {
        if (confirm('Are you sure you want to delete this boilerplate?')) {
            await boilerplatesStore.deleteBoilerplate(id);
        }
    }

    function handleInsertBoilerplate(boilerplate) {
        dispatch('insert', {
            name: boilerplate.name,
            content: boilerplate.content,
            blockType: boilerplate.blockType
        });
        closeModal();
    }

    function startEditingBoilerplate(boilerplate) {
        editingBoilerplateId = boilerplate.id;
        editingBoilerplateName = boilerplate.name;
        editingBoilerplateContent = boilerplate.content;
        // Hide add form if it's open
        showAddForm = false;
    }

    function cancelEditingBoilerplate() {
        editingBoilerplateId = null;
        editingBoilerplateName = '';
        editingBoilerplateContent = '';
    }

    async function handleSaveEdit() {
        if (!editingBoilerplateName.trim() || !editingBoilerplateContent.trim()) return;

        await boilerplatesStore.editBoilerplate(editingBoilerplateId, {
            name: editingBoilerplateName,
            content: editingBoilerplateContent
        });

        cancelEditingBoilerplate();
    }
</script>

<AccessibleModal 
    bind:show={show}
    variant="spec"
    title="Boilerplates"
    titleId="boilerplate-modal-title"
    on:close={closeModal}
    data-testid="boilerplate-modal"
>
    <div>
        <div class="modal-body">
            <div class="boilerplates-header flex flex-justify-between flex-align-center">
                <h3 class="m-0">Your Boilerplates</h3>
                <button class="add-button flex-center rounded-full cursor-pointer" on:click={toggleAddForm}>
                    {showAddForm ? '−' : '+'}
                </button>
            </div>

            {#if showAddForm}
                <div class="add-form w-full" transition:fade>
                    <input
                        class="w-full"
                        type="text"
                        placeholder="Boilerplate Name"
                        bind:value={newBoilerplateName}
                        aria-label="Boilerplate name"
                    />
                    <textarea
                        class="w-full"
                        placeholder="Boilerplate Content"
                        bind:value={newBoilerplateContent}
                        aria-label="Boilerplate content"
                    ></textarea>
                    <div class="form-actions flex gap-3">
                        <button class="modal-button modal-button-secondary" on:click={toggleAddForm}>Cancel</button>
                        <button 
                            class="modal-button modal-button-accent" 
                            on:click={handleAddBoilerplate}
                            disabled={!newBoilerplateName.trim() || !newBoilerplateContent.trim()}
                        >
                            Save
                        </button>
                    </div>
                </div>
            {/if}

            <div class="boilerplates-list flex flex-column gap-3">
                {#each $boilerplatesStore as boilerplate (boilerplate.id)}
                    {#if editingBoilerplateId === boilerplate.id}
                        <div class="edit-form w-full" transition:fade>
                            <input
                                class="w-full"
                                type="text"
                                placeholder="Boilerplate Name"
                                bind:value={editingBoilerplateName}
                                aria-label="Edit boilerplate name"
                            />
                            <textarea
                                class="w-full"
                                placeholder="Boilerplate Content"
                                bind:value={editingBoilerplateContent}
                                aria-label="Edit boilerplate content"
                            ></textarea>
                            <div class="form-actions flex gap-3">
                                <button class="modal-button modal-button-secondary" on:click={cancelEditingBoilerplate}>Cancel</button>
                                <button 
                                    class="modal-button modal-button-accent" 
                                    on:click={handleSaveEdit}
                                    disabled={!editingBoilerplateName.trim() || !editingBoilerplateContent.trim()}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    {:else}
                        <div class="boilerplate-item flex flex-justify-between flex-align-center">
                            <div class="boilerplate-info">
                                <h4 class="m-0">{boilerplate.name}</h4>
                                <p class="date">{new Date(boilerplate.createdAt).toLocaleDateString()}</p>
                            </div>
                            <div class="boilerplate-actions flex gap-2">
                                <button 
                                    class="modal-button modal-button-accent"
                                    on:click={() => handleInsertBoilerplate(boilerplate)}
                                >
                                    Use
                                </button>
                                <button 
                                    class="modal-button modal-button-secondary"
                                    on:click={() => startEditingBoilerplate(boilerplate)}
                                    aria-label="Edit boilerplate"
                                >
                                    Edit
                                </button>
                                <button 
                                    class="modal-button modal-button-danger"
                                    on:click={() => handleDeleteBoilerplate(boilerplate.id)}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    {/if}
                {/each}
            </div>
        </div>
    </div>
</AccessibleModal>

<style>
    .modal-body {
        padding: 1.5rem;
    }

    .boilerplates-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
    }

    .boilerplates-header h3 {
        margin: 0;
        font-size: 1.2rem;
        color: var(--color-text-primary);
    }

    .add-button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 32px;
        height: 32px;
        font-size: 1.2rem;
        color: var(--color-text-secondary);
        background-color: var(--color-surface-content-spec);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: 50%;
        transition: all 0.2s ease;
        cursor: pointer;
    }

    .add-button:hover {
        background-color: var(--color-surface-header-spec);
        border-color: var(--color-bg-panel-50);
    }

    .add-form,
    .edit-form {
        width: 100%;
        padding: 1.5rem; /* More padding */
        margin-bottom: 1.5rem;
        background-color: var(--color-surface-content-spec);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: 12px;
    }

    .edit-form {
        margin-bottom: 0;
    }

    .add-form input,
    .add-form textarea,
    .edit-form input,
    .edit-form textarea {
        width: 100%;
        padding: 0.75rem;
        margin-bottom: 1rem;
        font-size: 14px;
        color: var(--color-text-primary);
        background-color: var(--color-bg-input);
        border: none;
        border-radius: 8px;
        box-shadow: var(--shadow-inset-sm);
    }

    .add-form textarea,
    .edit-form textarea {
        min-height: 300px; /* Much taller */
        font-family: monospace; /* Better for code/text formatting */
        font-size: 14px;
        line-height: 1.5;
        transition: all 0.2s ease;
        resize: vertical;
    }
    
    .add-form input:focus,
    .add-form textarea:focus,
    .edit-form input:focus,
    .edit-form textarea:focus {
        background-color: var(--color-bg-interactive-hover);
        outline: none;
        box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 2px var(--color-bg-accent-50); /* Purple focus ring */
    }

    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
    }

    .boilerplates-list {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .boilerplate-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem;
        background-color: var(--color-surface-content-spec);
        border: 1px solid var(--color-bg-panel-30);
        border-radius: 12px;
    }

    .boilerplate-info h4 {
        margin: 0;
        font-size: 1rem;
        color: var(--color-text-primary);
    }

    .boilerplate-info .date {
        margin: 0.25rem 0 0;
        font-size: 0.8rem;
        color: var(--color-text-secondary);
    }

    .boilerplate-actions {
        display: flex;
        gap: 0.5rem;
    }

    /* Button sizes */
    .boilerplate-actions .modal-button {
        padding: 0.4rem 0.75rem;
        font-size: 12px;
    }
</style>